/**
 * The state screens. Each is a StatusScreen with its own copy and action.
 *
 * None of these shows a result, a score, a style or a factor value, and none
 * offers a retake. The only outbound link anywhere is the DNACare mailto on the
 * already-complete screen, which repeats two of the approved completion
 * sentences verbatim because they are exactly what that respondent needs to
 * know.
 */

import { useEffect, useState } from 'react';
import { SkeletonCard, StatusScreen } from '../components/StatusScreen.tsx';

const CARE_EMAIL = 'DNACare@dnabehavior.com';

export function Loading() {
  return <SkeletonCard />;
}

/** An address the app does not serve. Pages falls back to index.html for any path. */
export function NotFound() {
  return (
    <StatusScreen
      glyph="missing"
      eyebrow="Page not found"
      title="There is nothing at this address."
      actions={
        <a className="btn btn-primary" href="/">
          Go to the start
        </a>
      }
    >
      <p>
        The link may be incomplete. If you were sent a link to the Discovery, open it again from the
        message you received.
      </p>
    </StatusScreen>
  );
}

export function Unreachable({ onRetry }: { onRetry: () => void }) {
  return (
    <StatusScreen
      glyph="offline"
      tone="warning"
      eyebrow="Connection"
      title="Cannot reach the server"
      actions={
        <button className="btn btn-primary" type="button" onClick={onRetry}>
          Try again
        </button>
      }
    >
      <p>
        Your earlier answers are saved, so we will not start anything new until we can check with
        the server. Check your connection and try again.
      </p>
    </StatusScreen>
  );
}

/**
 * HTTP 429. Counts the server's retry_after down and offers the retry only
 * when it will succeed. Nothing the respondent did is lost.
 */
export function RateLimited({ retryAfter, onRetry }: { retryAfter: number; onRetry: () => void }) {
  const [remaining, setRemaining] = useState(Math.max(1, Math.round(retryAfter)));

  useEffect(() => {
    setRemaining(Math.max(1, Math.round(retryAfter)));
  }, [retryAfter]);

  useEffect(() => {
    if (remaining <= 0) return;
    const timer = window.setTimeout(() => setRemaining((r) => r - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [remaining]);

  const ready = remaining <= 0;
  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;
  const clock = `${minutes}:${String(seconds).padStart(2, '0')}`;

  return (
    <StatusScreen
      glyph="clock"
      tone="warning"
      eyebrow="Please wait"
      title="Too many attempts from this connection"
      actions={
        <button className="btn btn-primary" type="button" onClick={onRetry} disabled={!ready}>
          Try again
        </button>
      }
    >
      <p>
        Nothing has been lost. {ready ? 'You can continue now.' : 'You can continue in '}
        {ready ? null : <span className="countdown">{clock}</span>}
        {ready ? null : '.'}
      </p>
    </StatusScreen>
  );
}

/**
 * The server says this response is already complete, for example because it
 * was finished in another tab. No retake is offered, per spec FR7.
 */
export function AlreadyComplete() {
  return (
    <StatusScreen glyph="check" tone="brand" eyebrow="Already recorded" title="Your responses have already been recorded.">
      <p>Your results will be presented to you at the session.</p>
      <p>
        If you would like your own personal reports, email{' '}
        <a href={`mailto:${CARE_EMAIL}`}>{CARE_EMAIL}</a> and we will send them to you.
      </p>
    </StatusScreen>
  );
}

export function GenericError({ detail, onRetry }: { detail: string | null; onRetry: (() => void) | null }) {
  return (
    <StatusScreen
      glyph="alert"
      tone="danger"
      eyebrow="Something went wrong"
      title="That did not go through"
      actions={
        onRetry ? (
          <button className="btn btn-primary" type="button" onClick={onRetry}>
            Try again
          </button>
        ) : (
          <a className="btn btn-primary" href="/">
            Go to the start
          </a>
        )
      }
    >
      <p>Your earlier answers are saved. Please try again.</p>
      {detail ? <p className="small">{detail}</p> : null}
    </StatusScreen>
  );
}
